import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import './Results.css';
import DownloadConfirmModal from './DownloadConfirmModal';

function Results({ sessionId, trainingResults, threshold, onReset }) {
  const [predictionResults, setPredictionResults] = useState(null);
  const [histogramData, setHistogramData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  useEffect(() => {
    generatePredictions();
  }, [sessionId, threshold]);

  const generatePredictions = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use the threshold passed from DiagnosticPlots, or fall back to training results threshold
      const classificationThreshold = threshold || trainingResults?.best_threshold || 0.5;
      
      const response = await axios.post('/api/predict', {
        session_id: sessionId,
        threshold: classificationThreshold
      });
      setPredictionResults(response.data);
      
      // Fetch training probabilities for overlay
      const trainingProbs = await fetchTrainingProbabilities();
      
      // Create histogram data from probabilities
      if (response.data.probabilities) {
        createHistogram(response.data.probabilities, classificationThreshold, trainingProbs);
      }
      
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate predictions');
      setLoading(false);
    }
  };
  
  const fetchTrainingProbabilities = async () => {
    try {
      const response = await axios.get(`/api/training-probabilities/${sessionId}`);
      return response.data.probabilities;
    } catch (err) {
      console.log('Could not fetch training probabilities:', err);
      return null;
    }
  };
  
  const createHistogram = (probabilities, thresh, trainingProbs) => {
    // Calculate bin edges such that one edge falls exactly on the threshold
    const nbins = 30;
    const binWidth = 1.0 / nbins;
    
    // Find which bin the threshold falls into and adjust bins accordingly
    const threshBinIndex = Math.floor(thresh / binWidth);
    const binStart = threshBinIndex * binWidth;
    
    // Create bin edges starting from binStart
    const xbins = {
      start: 0,
      end: 1,
      size: binWidth
    };
    
    // Create separate traces for bars left and right of threshold
    const probsLeft = probabilities.filter(p => p < thresh);
    const probsRight = probabilities.filter(p => p >= thresh);
    
    const traces = [
      {
        x: probsLeft,
        type: 'histogram',
        xbins: xbins,
        name: 'Infalling',
        marker: {
          color: '#667eea',
          line: {
            color: '#ffffff',
            width: 0.5
          }
        },
        showlegend: false,
        hovertemplate: 'p(Backsplash): %{x:.3f}<br>Count: %{y}<extra></extra>'
      },
      {
        x: probsRight,
        type: 'histogram',
        xbins: xbins,
        name: 'Backsplash',
        marker: {
          color: '#ec4899',
          line: {
            color: '#ffffff',
            width: 0.5
          }
        },
        showlegend: false,
        hovertemplate: 'p(Backsplash): %{x:.3f}<br>Count: %{y}<extra></extra>'
      }
    ];
    
    const histData = {
      data: traces,
      layout: {
        template: 'plotly_dark',
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(30,30,50,0.5)',
        title: {
          text: 'Distribution of Backsplash Probabilities',
          font: { color: '#ffffff', size: 16 }
        },
        xaxis: {
          title: {
            text: 'p(Backsplash)',
            font: { color: '#ffffff', size: 14 }
          },
          gridcolor: 'rgba(255,255,255,0.1)',
          color: '#ffffff',
          range: [0, 1]
        },
        yaxis: {
          title: 'Count',
          gridcolor: 'rgba(255,255,255,0.1)',
          color: '#ffffff'
        },
        barmode: 'stack',
        shapes: [{
          type: 'line',
          x0: thresh,
          x1: thresh,
          y0: 0,
          y1: 1,
          yref: 'paper',
          line: {
            color: '#ffffff',
            width: 2
          }
        }],
        annotations: [
          {
            x: thresh - 0.02,
            y: 1,
            yref: 'paper',
            text: 'Infalling',
            showarrow: false,
            textangle: -90,
            font: { color: '#667eea', size: 13, family: 'Arial, sans-serif', weight: 400 },
            xanchor: 'center',
            yanchor: 'top'
          },
          {
            x: thresh + 0.02,
            y: 1,
            yref: 'paper',
            text: 'Backsplash',
            showarrow: false,
            textangle: -90,
            font: { color: '#ec4899', size: 13, family: 'Arial, sans-serif', weight: 400 },
            xanchor: 'center',
            yanchor: 'top'
          }
        ],
        margin: { l: 60, r: 30, t: 60, b: 60 },
        height: 400,
        width: 700,
        showlegend: false
      }
    };
    setHistogramData(histData);
  };

  const handleConfirmDownload = () => {
    setShowDownloadModal(false);
    handleDownload();
  };

  const handleDownload = () => {
    // Use fetch to download the file properly
    fetch(`/api/download/${sessionId}/predictions`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Download failed');
        }
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `backsplash_predictions.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Download error:', error);
        alert('Failed to download predictions. Please try again.');
      });
  };

  const plotNames = [
    { key: 'probability_distribution', title: 'Probability Distribution', description: 'Distribution of backsplash probabilities' },
    { key: 'classification_pie', title: 'Classification Summary', description: 'Breakdown of predictions' }
  ];

  if (loading) {
    return (
      <div className="results">
        <h2 className="section-title">Generating Predictions...</h2>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner"></div>
          <p style={{ marginTop: '20px', color: 'rgba(255, 255, 255, 0.7)' }}>Running classifier on your data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results">
        <h2 className="section-title">Error</h2>
        <div className="error">{error}</div>
        <div className="actions" style={{ marginTop: '30px' }}>
          <button className="button" onClick={onReset}>Start Over</button>
        </div>
      </div>
    );
  }

  if (!predictionResults) {
    return null;
  }

  return (
    <div className="results">
      <h2 className="section-title">🎉 Classification Complete!</h2>
      
      <div className="results-summary">
        <div className="summary-card">
          <div className="summary-icon">📊</div>
          <div className="summary-content">
            <h3>Classification Results</h3>
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-label">Total Galaxies:</span>
                <span className="stat-value">{predictionResults.n_total}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Backsplash:</span>
                <span className="stat-value backsplash">{predictionResults.n_backsplash}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Infalling:</span>
                <span className="stat-value infalling">{predictionResults.n_infalling}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Backsplash Fraction:</span>
                <span className="stat-value">{(predictionResults.backsplash_fraction * 100).toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">📈</div>
          <div className="summary-content">
            <h3>Model Performance</h3>
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-label">Validation Accuracy:</span>
                <span className="stat-value">{(trainingResults.test_accuracy * 100).toFixed(1)}%</span>
              </div>
              <div className="stat">
                <span className="stat-label">Train Accuracy:</span>
                <span className="stat-value">{(trainingResults.train_accuracy * 100).toFixed(1)}%</span>
              </div>
              <div className="stat">
                <span className="stat-label">Classification Threshold:</span>
                <span className="stat-value">{(threshold || trainingResults?.best_threshold || 0.5).toFixed(4)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {histogramData && (
        <div className="histogram-section">
          <Plot
            data={histogramData.data}
            layout={histogramData.layout}
            config={{
              responsive: true,
              displayModeBar: true,
              displaylogo: false,
              modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d', 'autoScale2d']
            }}
            style={{ width: '100%' }}
          />
        </div>
      )}

      <div className="download-section">
        <button className="button button-secondary" onClick={() => setShowDownloadModal(true)}>
          📥 Download Predictions CSV
        </button>
      </div>

      <div className="actions">
        <button className="button" onClick={onReset}>
          🔄 Classify Another Dataset
        </button>
      </div>

      <DownloadConfirmModal
        isOpen={showDownloadModal}
        onConfirm={handleConfirmDownload}
        onCancel={() => setShowDownloadModal(false)}
      />
    </div>
  );
}

export default Results;
